import Text from 'components/commons/text';
import * as React from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { CSS, VariantProps } from 'styles';
import typography from 'styles/typography';

import {
  StyledTabShadow,
  StyledTabPanelShadow,
  StyledTabsContainerShadow,
  StyledTabsListShadow,
} from './style';

type TabShadowProps = React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode;
};
type TabShadowVariants = VariantProps<typeof StyledTabShadow>;
export type ITabShadowProps = { css?: CSS } & TabShadowProps &
  TabShadowVariants;

const TabShadow = React.forwardRef<HTMLDivElement, ITabShadowProps>(
  (props, ref) => {
    const { children, ...rest } = props;
    return (
      <StyledTabShadow {...rest} ref={ref}>
        <Text
          css={{
            width: '100%',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            ...typography.buttonSmall,
            '@md': typography.buttonNormal,
          }}
          align="center"
        >
          {children}
        </Text>
      </StyledTabShadow>
    );
  },
);

TabShadow.displayName = 'TabShadow';

export type TabPanelShadowProps = {
  children: React.ReactNode;
  css?: CSS;
  className?: string;
};
type TabPanelShadowVariants = VariantProps<typeof StyledTabPanelShadow>;
export type ITabPanelShadowProps = TabPanelShadowProps & TabPanelShadowVariants;

const TabPanelShadow = React.forwardRef<HTMLDivElement, ITabPanelShadowProps>(
  (props, ref) => <StyledTabPanelShadow {...props} ref={ref} />,
);

TabPanelShadow.displayName = 'TabPanelShadow';

type TabsProps = {
  children: React.ReactNode;
  defaultActiveKey?: string;
  onChangeTab?: (activeKey: string) => void;
  tabMinWidth?: number;
  tabPanelCss?: CSS;
  tabPanelClassName?: string;
  tabListCss?: CSS;
  tabListClassName?: string;
  tabListItemCss?: CSS;
  tabListItemClassName?: string;
};
type CssProps = { css?: CSS };
type TabsContainerVariants = VariantProps<typeof StyledTabsContainerShadow>;

export type ITabsShadowProps = TabsProps & TabsContainerVariants & CssProps;

export const TabsShadow = React.forwardRef<HTMLDivElement, ITabsShadowProps>(
  (props, ref) => {
    const {
      children,
      defaultActiveKey,
      onChangeTab,
      tabMinWidth,
      tabPanelClassName,
      tabPanelCss,
      tabListClassName,
      tabListCss,
      tabListItemClassName,
      tabListItemCss,
      ...rest
    } = props;
    const list: any = children || [];
    const [activeKey, setActiveKey] = React.useState(
      defaultActiveKey || (list.length > 0 && list[0].key) || '',
    );
    const { width, ref: refResizeDetector } = useResizeDetector();
    const tabRef = React.useRef<any>([]);

    const toScroll = React.useMemo(() => {
      let totalTabWidth = 0;
      if (tabRef.current) {
        tabRef.current.map((item) => {
          totalTabWidth += item.offsetWidth;
        });
      }
      if (width) {
        if (width < totalTabWidth) {
          return true;
        }
      }
      return false;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [width, tabRef.current]);

    const onAddTabRef = React.useCallback((ef: any) => {
      if (ef && !tabRef.current.includes(ef)) {
        tabRef.current.push(ef);
      }
    }, []);

    return (
      <StyledTabsContainerShadow ref={refResizeDetector} {...rest}>
        <StyledTabsListShadow
          ref={ref}
          css={{ overflowX: toScroll ? 'scroll' : 'inherit', ...tabListCss }}
          className={tabListClassName}
        >
          {list.map((item, idx) => (
            <TabShadow
              key={item.key}
              onClick={() => {
                setActiveKey(item.key);
                onChangeTab && onChangeTab(item.key);
              }}
              isSelected={activeKey === item.key}
              css={{ ...tabListItemCss, minWidth: tabMinWidth }}
              className={tabListItemClassName}
              ref={onAddTabRef}
              tabIndex={idx + 1}
            >
              {item.props.title}
            </TabShadow>
          ))}
        </StyledTabsListShadow>
        {activeKey && !!list.find((item) => item.key === activeKey) && (
          <TabPanelShadow
            css={tabPanelCss}
            className={tabPanelClassName}
            key={activeKey || ''}
          >
            {list.find((item) => item.key === activeKey).props.children}
          </TabPanelShadow>
        )}
      </StyledTabsContainerShadow>
    );
  },
);

TabsShadow.displayName = 'TabsShadow';
